/**
 * Which broker account an order goes to — ROADMAP 4.11.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE SELECTION IS DELIBERATE AND IT IS REMEMBERED
 *
 * Remembered per browser, because a desk trades the same account all day and
 * re-picking it on every reload is the kind of friction that gets worked
 * around. NOT defaulted when there is more than one choice: an order placed
 * into the wrong account is not recoverable by undoing anything, so the
 * first one has to be chosen by a person.
 *
 * With exactly one connected account there is no choice to make, and that
 * one is selected.
 *
 * THE STORED VALUE IS VALIDATED, NOT TRUSTED
 *
 * `localStorage` survives the account being deleted, renamed or switched
 * from PAPER to LIVE. The id is checked against the live list on every load,
 * and a stale one selects nothing rather than silently resolving to whatever
 * now holds that id.
 *
 * THE STORE OWNS ITS OWN LOADING
 *
 * It did not, and that was a real defect. `Terminal.tsx` fetched
 * `/broker/connections` and pushed the result in here, which made a SCREEN
 * the authoritative owner of state the whole shell depends on. Land on
 * /portfolio from a cold reload and the store was empty, so the Portfolio
 * fetched nothing and read as flat, and the top bar's account selector
 * rendered nothing at all — both recovering only once the Terminal had been
 * visited and populated them.
 *
 * Loading belongs with the state. `load()` is single-flight, so several
 * screens mounting at once produce one request.
 * ─────────────────────────────────────────────────────────────────────────────
 */

import type { BrokerConnectionDto } from '@apna/shared'
import { api } from '@/lib/api'

const KEY = 'apna.selectedConnectionId'

export function readSelectedConnectionId(): string | null {
  try {
    return window.localStorage.getItem(KEY)
  } catch {
    // Private mode, or storage blocked. A forgotten selection is a minor
    // annoyance; a thrown exception on the order screen is not.
    return null
  }
}

export function writeSelectedConnectionId(id: string | null): void {
  try {
    if (id === null) window.localStorage.removeItem(KEY)
    else window.localStorage.setItem(KEY, id)
  } catch {
    // As above.
  }
}

/**
 * Which account should be selected, given what exists and what was stored.
 *
 * Pure, so the rules above are testable without a browser.
 */
export function resolveSelection(
  connections: BrokerConnectionDto[],
  storedId: string | null,
): string | null {
  const usable = connections.filter((c) => c.status !== 'DISABLED')
  if (usable.length === 0) return null

  const stored = storedId ? usable.find((c) => c.id === storedId) : undefined
  if (stored) return stored.id

  // Exactly one account is not a choice. More than one is, and it is the
  // user's — an order in the wrong account cannot be undone.
  return usable.length === 1 ? usable[0]!.id : null
}

/**
 * The active account's mode, for the top bar's badge.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * A TINY STORE RATHER THAN A PROP OR A SECOND FETCH.
 *
 * The badge lives in `TopBar`, which the app shell renders, while the
 * account is chosen on the Terminal. Threading it through the router would
 * mean lifting Terminal's state into the shell; fetching the connections
 * again in TopBar would mean two sources for one fact.
 *
 * It defaults to PAPER and NOT to null: an unknown mode rendered as nothing
 * is a top bar that quietly stops saying whether orders are real, and PAPER
 * is the safe thing to be wrong about.
 * ─────────────────────────────────────────────────────────────────────────────
 */
type ModeListener = () => void

/** What the top bar needs to render the account control and the badge. */
export interface AccountSelection {
  connections: BrokerConnectionDto[]
  selectedId: string | null
}

const EMPTY: AccountSelection = { connections: [], selectedId: null }

class AccountStore {
  private state: AccountSelection = EMPTY
  private loading: Promise<void> | null = null
  private hasLoaded = false
  private readonly listeners = new Set<ModeListener>()
  /** Set by the Terminal, which owns the selection. */

  subscribe = (fn: ModeListener): (() => void) => {
    this.listeners.add(fn)
    return () => this.listeners.delete(fn)
  }

  /**
   * Returns the SAME object until something changes.
   *
   * `useSyncExternalStore` compares snapshots by identity and would loop
   * forever on a fresh object each call.
   */
  getSnapshot = (): AccountSelection => this.state

  set(next: AccountSelection): void {
    if (
      next.selectedId === this.state.selectedId &&
      next.connections === this.state.connections
    ) {
      return
    }
    this.state = next
    this.emit()
  }

  /**
   * Fetch the connections and resolve the selection, once.
   *
   * Single-flight on the in-flight promise rather than on a "loaded" flag:
   * the app shell and a screen can both call this within the same tick, and
   * a flag set after the await lets both through.
   *
   * A failure leaves the store empty and is not retried here. The shell can
   * call `load()` again — the next screen mount does — and a screen that
   * needs an account already says so when it has none.
   */
  load(): Promise<void> {
    if (this.loading) return this.loading
    this.loading = api
      .get<{ items: BrokerConnectionDto[] }>('/broker/connections')
      .then((r) => {
        // The stored id is CHECKED against the live list, never trusted:
        // localStorage outlives an account being deleted or switched to LIVE.
        this.set({ connections: r.items, selectedId: resolveSelection(r.items, readSelectedConnectionId()) })
      })
      .catch(() => {
        // The chain still works without an account; only trading does not.
        this.set(EMPTY)
      })
      .finally(() => {
        // Set HERE, not in `set`: `set` short-circuits when nothing changed,
        // so a load that legitimately returns the same empty list would
        // otherwise never record that it ran.
        this.hasLoaded = true
        this.loading = null
      })
    return this.loading
  }

  /** True once a load has finished, successfully or not. */
  loaded(): boolean {
    return this.hasLoaded
  }

  /**
   * Choose an account. The one place the selection is written.
   *
   * Persisted here rather than by the caller, so a screen cannot change the
   * selection without it being remembered — the two were separate and a
   * second picker would have had to remember to do both.
   */
  select(id: string | null): void {
    writeSelectedConnectionId(id)
    this.set({ connections: this.state.connections, selectedId: id })
  }

  mode(): ExecutionModeValue {
    const c = this.state.connections.find((x) => x.id === this.state.selectedId)
    // PAPER when nothing is chosen: an unknown mode must never read as live.
    return c?.mode === 'LIVE' ? 'LIVE' : 'PAPER'
  }

  private emit(): void {
    for (const fn of this.listeners) fn()
  }
}

type ExecutionModeValue = 'LIVE' | 'PAPER'

export const accountStore = new AccountStore()
